import { useEffect, useRef, useState } from 'react';

/**
 * @typedef IntersectionObserverInitDocs
 * @type {object}
 * @property [root]
 * @property {string} [rootMargin]
 * @property {number|number[]} [threshold]
 */

/**
 * @typedef MutableRefObjectDocs
 * @type {object}
 * @property current
 */

/** Хук определяет явлется элемент позиционированным со значением "sticky"
 * @param {IntersectionObserverInitDocs} [options={}]
 * @param {(isSticky: boolean) => void} [onStickyChange] - Необязательная функция обратного вызова для обработки изменения isSticky.
 * @returns {[MutableRefObjectDocs, boolean]}
 *  */
export const useSticky = (
  options: IntersectionObserverInit & {
    topOffset?: number;
    onStickyChange?: (isSticky: boolean) => void;
  } = {},
): [React.MutableRefObject<null>, boolean, boolean] => {
  const ref = useRef(null);
  const [isSticky, setIsSticky] = useState(true);
  const [isTop, setIsTop] = useState(true);

  const {
    threshold = 1,
    root,
    rootMargin = '0%',
    topOffset = 1,
    onStickyChange,
  } = options;

  useEffect(() => {
    if (!ref.current || root === undefined) {
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => setIsTop(event.intersectionRatio < 1),
      { threshold: 1, root, rootMargin: `-${topOffset}px 0px 0px 0px` },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [root, topOffset]);

  useEffect(() => {
    if (!ref.current || root === undefined) {
      return;
    }

    const observer = new IntersectionObserver(
      ([event]) => {
        const newIsSticky = event.intersectionRatio === 1;

        setIsSticky(newIsSticky);
        onStickyChange?.(newIsSticky);
      },
      { threshold, root, rootMargin },
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [threshold, root, rootMargin, onStickyChange]);

  return [ref, isTop, isSticky];
};
